import Footer, { type FooterProps } from 'components/Footer';
import type { HeaderNav } from 'interfaces/navigation/Header';
import Header from 'components/Header';
import { LayoutWrapper, LeaderboardAdWrapper, NativoLeaderboard } from 'components/Layout/LayoutStyles';
import { GoogleAnalytics } from '@next/third-parties/google';

import { Leaderboard } from 'components/Ad/AdSlot';
import { PageType } from 'interfaces/content/articles/Post';
import RecommendsFooter, { type RecommendsFooterProps } from 'components/RecommendsFooter';
import { Microsites } from 'constants/constants';
import type { Hub } from 'interfaces/Home';
import { useRouter } from 'next/router';
import type { JSX } from 'react';
import PaywallaxMarkII from 'components/PaywallaxMarkII';
import { getLocalStorageObject, updateLocalStorageObject } from 'utils/localStorageUtils';
import StickyFooter from 'components/Ad/AdSlot/StickyFooter';
import useBreakpoint from 'hooks/useBreakpoint';
import { UserContext } from 'providers/UserProvider';
import { useContext, useEffect } from 'react';

import { trackOptimizelyEvent } from 'providers/OptimizelyProvider';
import GoogleOneTap from '@/main/lib/services/GoogleOneTap';
import { setLeaderboardHeight } from '@/main/lib/services/Ad/utils/leaderboardManager';
import type { PageAdConfig } from '@/main/entities/ads/Ad';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface LayoutProps {
  children: JSX.Element;
  headerNav: HeaderNav;
  pageAdConfig?: PageAdConfig;
  leaderboard: boolean;
  footer?: FooterProps | RecommendsFooterProps;
  hasVanityUrl: boolean | undefined;
  pageType: PageType;
  currentSection: string;
  section?: Hub;
  currentTags: string[];
  currentAuthors?: string;
  hideHeaderFooter?: boolean;
}

const Layout = (props: LayoutProps) => {
  const {
    children,
    leaderboard,
    footer,
    headerNav,
    pageAdConfig,
    hasVanityUrl,
    section,
    currentTags,
    hideHeaderFooter,
    pageType,
  } = props;
  const isMobile = useBreakpoint('mmMax');
  const router = useRouter();
  const isRecommends = [Microsites.Recommends, Microsites.Education].includes(headerNav?.subDomain);
  const showRecommendsFooter = process.env.FEATURE_FLAG_RECOMMENDS_FOOTER && isRecommends;
  const { showPaywallax } = useContext(UserContext);

  const isPianoTemplate = ['/subscribe/', '/paid-subscribe', '/email-subscribe/'].some((path) =>
    router.asPath.includes(path),
  );

  useEffect(() => {
    const checkoutTrackingProps = getLocalStorageObject('checkoutTrackingProps');
    if (checkoutTrackingProps?.updatedAt && Date.now() - checkoutTrackingProps.updatedAt > 24 * 60 * 60 * 1000) {
      // Clear out old tracking exposure and referral param properties after 24 hours
      updateLocalStorageObject('checkoutTrackingProps', {
        checkout_referral_params: null,
        experience_id: null,
        experience_action_id: null,
        template_id: null,
        template_variant_id: null,
        offer_id: null,
        persistExposureProps: null,
        persistReferralParams: null,
        login_method: null,
        login_type: null,
        lts: null,
        terms: null,
      });
    }
  }, []);

  if (hideHeaderFooter || isPianoTemplate) {
    return (
      <div>
        {children}
        <PaywallaxMarkII />
      </div>
    );
  }
  const { userFlags: userVariants } = useOptimizely();
  const { articleStickyFooter } = userVariants as { articleStickyFooter: { enabled: boolean } };
  const hasStickyFooter = articleStickyFooter?.enabled;
  const showStickyFooter = hasStickyFooter && isMobile && pageType === PageType.ARTICLE;

  if (showStickyFooter) {
    setLeaderboardHeight(0);
  }

  useEffect(() => {
    const handleClick = (e) => {
      const interactiveElement = e.target.closest('a, button, input, textarea, select, [role="button"]');
      if (!interactiveElement) {
        return;
      }

      trackOptimizelyEvent('page_clicks', {
        dataCy: interactiveElement.dataset.cy || '',
        href: interactiveElement.getAttribute('href') || '',
        location: window.location.pathname,
        tag: interactiveElement.tagName.toLowerCase(),
        text: interactiveElement.textContent?.trim() || '',
      });
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <div>
      <GoogleAnalytics gaId={process.env.G_ANALYTICS_ID || ''} />
      {leaderboard && !showStickyFooter && (
        <LeaderboardAdWrapper className='leaderboard-ad-wrapper-parent'>
          <Leaderboard id={0} />
        </LeaderboardAdWrapper>
      )}
      {hasVanityUrl && <NativoLeaderboard className='nativo-content__leaderboard' />}
      {headerNav && !isPianoTemplate && (
        <Header
          {...headerNav}
          pageAdConfig={pageAdConfig}
          topics={headerNav.topicNavigation}
          currentTags={currentTags}
          showStickyFooter={showStickyFooter}
        />
      )}
      <LayoutWrapper
        $isRecommends={isRecommends}
        $showRecommendsFooter={showRecommendsFooter}
        $isPianoTemplate={isPianoTemplate}
      >
        <main>{children}</main>
        {!showRecommendsFooter && footer && !isPianoTemplate && <Footer {...(footer as FooterProps)} />}
        {showRecommendsFooter && footer && (
          <RecommendsFooter
            {...(footer as RecommendsFooterProps)}
            currentSection={section}
          />
        )}

        {showStickyFooter && !showPaywallax && <StickyFooter id={0} />}
      </LayoutWrapper>
      <GoogleOneTap
        clientId={process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || ''}
        pageType={pageType}
      />
      <PaywallaxMarkII />
    </div>
  );
};

export default Layout;
